import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, toastError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LoadingState, EmptyState } from "./States";
import { MessageSquare, Phone, Mail, Users, FileText, ArrowRightLeft, Cog, Truck, AlertTriangle, ClipboardList, Boxes, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ICONS = { notat: MessageSquare, telefon: Phone, "e-post": Mail, møte: Users, dokument: FileText, statusendring: ArrowRightLeft, system: Cog, transport: Truck, avvik: AlertTriangle, oppgave: ClipboardList, allokering: Boxes, endring: Pencil };
const TYPES = ["notat", "telefon", "e-post", "møte"];

export const ActivityFeed = ({ entityType, entityId, allowAdd = true }) => {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [type, setType] = useState("notat");
  const key = ["activities", entityType, entityId];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => get("/activities", { entity_type: entityType, entity_id: entityId }), enabled: !!entityId });
  const add = useMutation({
    mutationFn: () => post("/activities", { entity_type: entityType, entity_id: entityId, type, text }),
    onSuccess: () => {
      setText("");
      qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toastError(e),
  });
  return (
    <div data-testid="activity-feed">
      {allowAdd && user && (
        <div className="mb-4 rounded-md border p-3 bg-muted/30">
          <Textarea data-testid="activity-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Skriv notat, logg samtale eller møte…" className="bg-card" />
          <div className="mt-2 flex items-center justify-between gap-2">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="w-32 h-8" data-testid="activity-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" disabled={!text.trim() || add.isPending} onClick={() => add.mutate()} data-testid="activity-submit-button">Logg</Button>
          </div>
        </div>
      )}
      {isLoading ? (
        <LoadingState className="py-6" />
      ) : !data?.length ? (
        <EmptyState title="Ingen aktivitet ennå" icon={MessageSquare} className="py-8" testId="activity-empty" />
      ) : (
        <ol className="relative border-l ml-2 space-y-4">
          {data.map((a) => {
            const Icon = ICONS[a.type] || MessageSquare;
            return (
              <li key={a.id} className="ml-4" data-testid="activity-item">
                <span className="absolute -left-[9px] mt-1 flex h-4 w-4 items-center justify-center rounded-full bg-card ring-2 ring-border">
                  <Icon className="h-2.5 w-2.5 text-muted-foreground" />
                </span>
                <div className="text-sm">{a.text}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{a.user_name || "System"} · {fmtDateTime(a.created_at)} · <span className="capitalize">{a.type}</span>{a.entity_type !== entityType ? ` · fra ${a.entity_type === "lead" ? "henvendelse" : "prosjekt"}` : ""}</div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export const AuditTrail = ({ entityType, entityId }) => {
  const { data, isLoading } = useQuery({ queryKey: ["audit", entityType, entityId], queryFn: () => get("/audit", { entity_type: entityType, entity_id: entityId }), enabled: !!entityId });
  if (isLoading) return <LoadingState className="py-6" />;
  if (!data?.length) return <EmptyState title="Ingen endringshistorikk" icon={Pencil} className="py-8" testId="audit-empty" />;
  return (
    <ul className="divide-y rounded-md border text-sm" data-testid="audit-trail">
      {data.map((a) => (
        <li key={a.id} className="px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <div className="font-medium">
              <span className="font-mono text-[12px] text-muted-foreground mr-2">{a.action}</span>
              {a.summary}
            </div>
            <div className="text-xs text-muted-foreground whitespace-nowrap">{a.user_name} · {fmtDateTime(a.timestamp)}</div>
          </div>
          {a.changes?.length > 0 && (
            <ul className="mt-1 text-xs text-muted-foreground space-y-0.5">
              {a.changes.slice(0, 8).map((c, i) => (
                <li key={i} className="font-mono">
                  {c.field}: <span className="line-through opacity-70">{fmtVal(c.old)}</span> → <span className="text-foreground">{fmtVal(c.new)}</span>
                </li>
              ))}
              {a.changes.length > 8 && <li>+{a.changes.length - 8} flere felt</li>}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
};

function fmtVal(v) {
  if (v === null || v === undefined) return "–";
  if (typeof v === "object") return JSON.stringify(v).slice(0, 60);
  return String(v).slice(0, 60);
}
